<template>
  <div class="cap">
    <button
      type="button"
      class="cap__play"
      :aria-label="playing ? $t('chat.audio.pause') : $t('chat.audio.play')"
      @click="toggle"
    >
      <feather-icon
        :icon="playing ? 'PauseIcon' : 'PlayIcon'"
        size="16"
      />
    </button>

    <div class="cap__body">
      <div
        ref="track"
        class="cap__wave"
        role="slider"
        :aria-valuemin="0"
        :aria-valuemax="Math.round(total)"
        :aria-valuenow="Math.round(current)"
        @click="seek"
      >
        <i
          v-for="(level, i) in bars"
          :key="i"
          :class="{ 'cap__bar--on': (i + 0.5) / bars.length * 100 <= progress }"
          :style="{ height: `${Math.max(12, level * 100)}%` }"
        />
      </div>
      <div class="cap__meta">
        <span>{{ clock(playing || current > 0 ? current : total) }}</span>
        <button
          v-if="playing || current > 0"
          type="button"
          class="cap__speed"
          @click="cycleSpeed"
        >{{ speed }}×</button>
      </div>
    </div>

    <audio
      ref="audio"
      :src="src"
      preload="metadata"
      @loadedmetadata="onMeta"
      @timeupdate="current = $refs.audio.currentTime"
      @ended="onEnded"
      @pause="playing = false"
      @play="playing = true"
    />
  </div>
</template>

<script>
import { formatDuration } from '@/utils/chat'

const SPEEDS = [1, 1.5, 2]
const BARS = 40
// Picos por URL: o mesmo áudio reaparece ao rolar a conversa e não precisa ser decodificado de novo.
const peaksCache = {}

// Silhueta provisória (e reserva se o arquivo não puder ser decodificado), estável para o mesmo áudio.
function placeholderPeaks(seed) {
  let h = 0
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) % 4294967296
  return Array.from({ length: BARS }, () => {
    h = (h * 1664525 + 1013904223) % 4294967296

    return 0.25 + (h / 4294967296) * 0.55
  })
}

async function decodePeaks(url) {
  const Context = window.AudioContext || window.webkitAudioContext
  const response = await fetch(url)
  const context = new Context()
  try {
    const buffer = await context.decodeAudioData(await response.arrayBuffer())
    const data = buffer.getChannelData(0)
    const size = Math.floor(data.length / BARS)
    const peaks = Array.from({ length: BARS }, (_, i) => {
      let sum = 0
      for (let j = i * size; j < (i + 1) * size; j += 1) sum += Math.abs(data[j])

      return size ? sum / size : 0
    })
    const max = Math.max(...peaks) || 1

    return peaks.map(v => v / max)
  } finally {
    context.close().catch(() => {})
  }
}

export default {
  name: 'ChatAudioPlayer',

  props: {
    src: { type: String, required: true },
    // Duração informada por quem gravou; o arquivo gravado no navegador (webm) não traz a duração nos metadados.
    duration: { type: Number, default: 0 },
  },

  data() {
    return {
      playing: false, current: 0, metaDuration: 0, speed: 1, peaks: null,
    }
  },

  computed: {
    bars() {
      return this.peaks || placeholderPeaks(this.src)
    },
    total() {
      return this.duration || (Number.isFinite(this.metaDuration) ? this.metaDuration : 0)
    },
    progress() {
      return this.total ? Math.min(100, (this.current / this.total) * 100) : 0
    },
  },

  beforeDestroy() {
    if (this.$refs.audio) this.$refs.audio.pause()
  },

  created() {
    this.$root.$on('chat-audio-play', this.stopFor)
    this.loadPeaks()
  },

  destroyed() {
    this.$root.$off('chat-audio-play', this.stopFor)
  },

  methods: {
    clock: formatDuration,

    async loadPeaks() {
      // Mensagem ainda subindo (blob local) também decodifica; qualquer falha mantém a silhueta provisória.
      if (!peaksCache[this.src]) peaksCache[this.src] = decodePeaks(this.src).catch(() => null)
      this.peaks = await peaksCache[this.src]
    },

    toggle() {
      const { audio } = this.$refs
      if (!audio) return
      if (this.playing) {
        audio.pause()
        return
      }
      // Só um áudio toca por vez.
      this.$root.$emit('chat-audio-play', this)
      audio.playbackRate = this.speed
      audio.play().catch(() => { this.playing = false })
    },

    seek(event) {
      const rect = this.$refs.track.getBoundingClientRect()
      const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width))
      this.$refs.audio.currentTime = ratio * this.total
      this.current = this.$refs.audio.currentTime
    },

    cycleSpeed() {
      this.speed = SPEEDS[(SPEEDS.indexOf(this.speed) + 1) % SPEEDS.length]
      this.$refs.audio.playbackRate = this.speed
    },

    onMeta() {
      this.metaDuration = this.$refs.audio.duration
    },

    onEnded() {
      this.playing = false
      this.current = 0
    },

    stopFor(other) {
      if (other !== this && this.playing) this.$refs.audio.pause()
    },
  },
}
</script>

<style scoped>
.cap {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 220px;
  max-width: 280px;
}

.cap__play {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 50%;
  background: #7367f0;
  color: #fff;
  transition: transform 0.12s ease, background 0.12s ease;
}

.cap__play:hover { background: #5e50ee; }
.cap__play:active { transform: scale(0.94); }

.cap__body { flex: 1; min-width: 0; }

.cap__wave {
  display: flex;
  align-items: center;
  gap: 2px;
  height: 28px;
  cursor: pointer;
}

.cap__wave i {
  flex: 1;
  min-width: 2px;
  border-radius: 2px;
  background: rgba(115, 103, 240, 0.3);
  transition: background 0.1s;
}

.cap__wave i.cap__bar--on { background: #7367f0; }

.cap__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.72rem;
  font-variant-numeric: tabular-nums;
  opacity: 0.75;
}

.cap__speed {
  padding: 0 6px;
  border: 0;
  border-radius: 8px;
  background: rgba(115, 103, 240, 0.18);
  color: inherit;
  font-size: 0.7rem;
  font-weight: 700;
}
</style>
